import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Loader2, Sparkles, ArrowRight, ArrowLeft, ExternalLink, Circle,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import {
  onboardingApi, calendarConnectApi, driversApi, dailyPlanApi,
  dailyFocusBlocksApi, calendarApi,
} from '../api/services';
import type { OnboardingState, AnnualStrategicDriver } from '../types/models';
import { colourForDriverId } from '../theme/tokens';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DayRibbon, type DayRibbonSegment } from '../components/dashboard/DayRibbon';
import { cn } from '@/lib/utils';
import { useEntitlements } from '../context/EntitlementsContext';

// ---------------------------------------------------------------------------
// OnboardingWizardPage — the guided first-run (ONBOARDING_BUILD_SPEC, 8 steps).
// State is server-side (resumable) via onboardingApi; each step commits its own
// slice on Continue. Steps 1–6 form the tracked "setup" rail; 0 + 7 are bookends.
// ---------------------------------------------------------------------------

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const SUGGESTED_DRIVERS = [
  'Grow revenue', 'Customer for life', 'Operational excellence',
  'Build the team', 'Product & innovation', 'Financial discipline',
];

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

const STEP_LABELS = ['Calendar', 'Claude', 'Protect', 'Focus', 'Drivers', 'Build'];
const DRIVERS_STEP = 5;

function focusCaption(h: number): string {
  if (h < 8) return 'Gentle and sustainable.';
  if (h <= 13) return 'Strong, sustainable.';
  if (h <= 18) return 'Ambitious — defended hard.';
  return 'Deep-work heavy.';
}

function nextWorkingDayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function hhmmToMinutes(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const { markOnboardingComplete } = useAuth();

  const [state, setState] = useState<OnboardingState | null>(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Strategic drivers are a leadership tool: on a plan without them
  // (Personal by default) the Drivers step is skipped. null until the plan is
  // known (the step stays, nothing is loaded). There is no EA or assignments
  // step to skip: onboarding is for the signed-in person only.
  const entitlements = useEntitlements();
  const hasDrivers: boolean | null = entitlements.status === 'unknown'
    ? null
    : entitlements.known('strategicDrivers') !== false;

  // Step-local UI state
  const [connecting, setConnecting] = useState<string | null>(null);
  const [workStart, setWorkStart] = useState('08:00');
  const [workEnd, setWorkEnd] = useState('18:00');
  const [schoolRun, setSchoolRun] = useState(true);
  const [eveningsWeekends, setEveningsWeekends] = useState(true);
  const [lunch, setLunch] = useState(false);
  const [focusHours, setFocusHours] = useState(12);
  const [autoAllocate, setAutoAllocate] = useState(true);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  const [customDriver, setCustomDriver] = useState('');
  const [claudeUrl, setClaudeUrl] = useState('https://claude.ai/settings/connectors');

  // Step-6 payoff
  const [buildPhase, setBuildPhase] = useState(0);
  const [ribbon, setRibbon] = useState<DayRibbonSegment[] | null>(null);
  const [protectedMinutes, setProtectedMinutes] = useState(0);

  // ── Load server state (resume) + handle OAuth return ──────────────────────
  const hydrate = useCallback((s: OnboardingState) => {
    setState(s);
    setWorkStart(s.workingHoursStart || '08:00');
    setWorkEnd(s.workingHoursEnd || '18:00');
    setFocusHours(s.focusTargetHours ?? 12);
    setAutoAllocate(s.autoAllocate);
    setSelectedDrivers(s.selectedDriverIds || []);
    setSchoolRun(s.protectedBlocks?.some((b) => b.kind === 'SchoolRun') ?? true);
    setLunch(s.protectedBlocks?.some((b) => b.kind === 'Lunch') ?? false);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await onboardingApi.getState();
        hydrate(s);
        const params = new URLSearchParams(window.location.search);
        const cameBack = params.get('connected') === 'microsoft' || params.get('connected') === 'google';
        if (cameBack) {
          window.history.replaceState({}, '', window.location.pathname);
          const updated = await onboardingApi.patchState({ calendarConnected: true, currentStep: 2 });
          hydrate(updated);
          setStep(2);
        } else {
          setStep(Math.min(s.currentStep ?? 0, 7));
        }
      } catch (e: any) {
        setError('Could not load your setup. Please refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, [hydrate]);

  useEffect(() => {
    onboardingApi.claudeConnection().then((c) => setClaudeUrl(c.connectorUrl)).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasDrivers) driversApi.getAll().then(setDrivers).catch(() => {});
  }, [hasDrivers]);

  // A resumed setup parked on the Drivers step of a plan without drivers.
  useEffect(() => {
    if (hasDrivers === false && step === DRIVERS_STEP) setStep(DRIVERS_STEP - 1);
  }, [hasDrivers, step]);

  const commit = useCallback(async (patch: Parameters<typeof onboardingApi.patchState>[0], next: number) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await onboardingApi.patchState({ ...patch, currentStep: next });
      hydrate(updated);
      setStep(next);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }, [hydrate]);

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const handleSkip = async () => {
    setSaving(true);
    try {
      await onboardingApi.skip();
      markOnboardingComplete();   // release the gate without a reload
      navigate('/');
    } catch {
      setError('Could not skip. Try again.');
      setSaving(false);
    }
  };

  // ── Step 1 — calendar OAuth ───────────────────────────────────────────────
  const connectCalendar = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setError(null);
    try {
      await onboardingApi.patchState({ currentStep: 1 }); // persist where we are
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft({ name })
        : await calendarConnectApi.connectGoogle({ name });
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Couldn’t connect — try again.');
      setConnecting(null);
    }
  };

  // ── Step 3 — protected blocks reconcile ───────────────────────────────────
  const commitProtectHours = async () => {
    setSaving(true);
    setError(null);
    try {
      const existing = state?.protectedBlocks || [];
      const hasKind = (k: string) => existing.some((b) => b.kind === k);
      // School run — drop-off + pick-up holds on weekdays.
      if (schoolRun && !hasKind('SchoolRun')) {
        await onboardingApi.createProtectedBlock({ label: 'The school run', weekdays: WEEKDAYS, startTime: '08:30', durationMinutes: 30, isEnabled: true, kind: 'SchoolRun' });
        await onboardingApi.createProtectedBlock({ label: 'The school run', weekdays: WEEKDAYS, startTime: '15:30', durationMinutes: 30, isEnabled: true, kind: 'SchoolRun' });
      } else if (!schoolRun && hasKind('SchoolRun')) {
        for (const b of existing.filter((b) => b.kind === 'SchoolRun')) await onboardingApi.deleteProtectedBlock(b.id);
      }
      // Lunch — a daily protected break.
      if (lunch && !hasKind('Lunch')) {
        await onboardingApi.createProtectedBlock({ label: 'Lunch', weekdays: WEEKDAYS, startTime: '12:30', durationMinutes: 60, isEnabled: true, kind: 'Lunch' });
      } else if (!lunch && hasKind('Lunch')) {
        for (const b of existing.filter((b) => b.kind === 'Lunch')) await onboardingApi.deleteProtectedBlock(b.id);
      }
      await commit({ workingHoursStart: workStart, workingHoursEnd: workEnd }, 4);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not save. Try again.');
      setSaving(false);
    }
  };

  // ── Step 5 → 6 — build the first day ──────────────────────────────────────
  const buildFirstDay = async (patch: Parameters<typeof onboardingApi.patchState>[0] = {}) => {
    // Persist driver selection, advance to the payoff, then do the real work.
    await commit({ ...patch, selectedDriverIds: selectedDrivers }, 6);
    setBuildPhase(0);
    setRibbon(null);
    const date = nextWorkingDayISO();
    try {
      // Sequence the reassuring checklist while the plan generates.
      const tick = setInterval(() => setBuildPhase((p) => Math.min(p + 1, 4)), 700);
      await dailyPlanApi.generate(date, { trigger: 'onboarding' }).catch(() => {});
      clearInterval(tick);
      setBuildPhase(4);

      const [blocks, events] = await Promise.all([
        dailyFocusBlocksApi.getByDate(date).catch(() => []),
        calendarApi.getByRange(date, date).catch(() => []),
      ]);
      const segs: DayRibbonSegment[] = [];
      let mins = 0;
      for (const b of blocks) {
        const s = hhmmToMinutes(b.startTime); const e = hhmmToMinutes(b.endTime);
        if (e > s) { segs.push({ kind: 'focus', startMinutes: s, endMinutes: e, title: b.label || 'Focus', id: b.id }); mins += e - s; }
      }
      for (const ev of events as any[]) {
        if (ev.isAllDay) continue;
        const sd = new Date(ev.startTime); const ed = new Date(ev.endTime);
        const s = sd.getHours() * 60 + sd.getMinutes(); const e = ed.getHours() * 60 + ed.getMinutes();
        if (e > s) segs.push({ kind: ev.isPersonalEvent ? 'personal' : 'meeting', startMinutes: s, endMinutes: e, title: ev.title || 'Meeting', id: ev.id });
      }
      setProtectedMinutes(mins);
      setRibbon(segs.sort((a, b) => a.startMinutes - b.startMinutes));
    } catch {
      setRibbon([]); // graceful: still show the Done step
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await onboardingApi.complete();
      markOnboardingComplete();   // release the gate without a reload
      navigate('/');
    } catch {
      setError('Could not finish. Try again.');
      setSaving(false);
    }
  };

  const toggleDriver = (id: string) =>
    setSelectedDrivers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const addCustomDriver = async () => {
    const title = customDriver.trim();
    if (!title) return;
    try {
      const created = await driversApi.create({ title });
      setDrivers((d) => [...d, created]);
      setSelectedDrivers((s) => [...s, created.id]);
      setCustomDriver('');
    } catch {
      setError('Could not add that driver.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070A12] text-white">
        <Loader2 className="h-6 w-6 animate-spin opacity-70" />
      </div>
    );
  }

  const protectedHrs = Math.round((protectedMinutes / 60) * 10) / 10;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#070A12] via-[#0B1120] to-[#070A12] text-white">
      <div className="mx-auto flex min-h-screen max-w-[640px] flex-col px-6 py-8">
        {/* Top bar */}
        <div className="mb-6 flex items-center justify-between">
          <span className="text-sm font-semibold tracking-tight opacity-90">Cadence <span className="opacity-50">· by Stratium</span></span>
          {step >= 1 && step <= 6 && (
            <button onClick={handleSkip} disabled={saving} className="text-xs text-white/50 hover:text-white/80">Skip setup</button>
          )}
        </div>

        {/* Progress rail (steps 1–6) */}
        {step >= 1 && step <= 6 && (
          <div className="mb-8 flex items-center gap-1.5">
            {STEP_LABELS.map((label, i) => ({ label, n: i + 1 }))
              .filter(({ n }) => hasDrivers !== false || n !== DRIVERS_STEP)
              .map(({ label, n }) => {
              return (
                <div key={label} className="flex flex-1 flex-col items-center gap-1">
                  <div className={cn('h-1 w-full rounded-full', step > n ? 'bg-emerald-400' : step === n ? 'bg-[#7DB1FF]' : 'bg-white/10')} />
                  <span className={cn('text-[10px]', step === n ? 'text-[#7DB1FF]' : 'text-white/40')}>{label}</span>
                </div>
              );
            })}
          </div>
        )}

        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="flex-1">
          {/* Step 0 — Welcome */}
          {step === 0 && (
            <div className="flex flex-col items-start gap-5 pt-12">
              <h1 className="font-serif text-4xl font-light leading-tight">Let’s give you your time back.</h1>
              <p className="max-w-md text-white/70">A calm, guided setup. Six short steps, about three minutes. When we’re done, your day will already be protected, and Claude will be working quietly on your behalf.</p>
              <Button onClick={() => commit({}, 1)} disabled={saving} className="mt-2 gap-2 bg-[#7DB1FF] text-[#070A12] hover:bg-[#A9CBFF]">
                Begin setup <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Step 1 — Calendar */}
          {step === 1 && (
            <StepShell eyebrow="Step 1 · Your calendar" title="Start with your day."
              sub="Cadence reads your calendar to see where your time goes, then protects what matters. Read and write, so it can defend focus blocks for you. Nothing is shared.">
              <div className="grid gap-3 sm:grid-cols-2">
                <ConnectCard icon={<MicrosoftIcon />} title="Microsoft 365" subtitle="Outlook calendar + mail"
                  busy={connecting === 'microsoft'} done={state?.calendarConnected} onClick={() => connectCalendar('microsoft')} />
                <ConnectCard icon={<GoogleIcon />} title="Google Calendar" subtitle="Calendar + Gmail"
                  busy={connecting === 'google'} done={state?.calendarConnected} onClick={() => connectCalendar('google')} />
              </div>
              <NavRow onBack={goBack} onNext={() => commit({}, 2)} nextDisabled={!state?.calendarConnected || saving} />
            </StepShell>
          )}

          {/* Step 2 — Claude */}
          {step === 2 && (
            <StepShell eyebrow="Step 2 · Claude" title="Your quiet chief of staff."
              sub="Cadence already drafts your day and sorts your captures using Claude. Add Cadence as a connector inside Claude to query your day from there too.">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                {[['Drafts your day.', 'Reads your calendar and builds a time-blocked plan each morning.'],
                  ['Turns talk into tasks.', 'Forward an email or drop a transcript; Claude pulls out the actions.'],
                  ['Protects your focus.', 'It defends your deep-work blocks and reschedules around them.']].map(([lead, line]) => (
                  <div key={lead} className="mb-3 flex gap-2 last:mb-0">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#7DB1FF]" />
                    <p className="text-sm"><span className="font-semibold">{lead}</span> <span className="text-white/60">{line}</span></p>
                  </div>
                ))}
              </div>
              <a href={claudeUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => onboardingApi.patchState({ claudeConnectionMode: 'mcp' }).catch(() => {})}
                className="inline-flex items-center gap-1.5 text-sm text-[#7DB1FF] hover:underline">
                Add Cadence to Claude <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <NavRow onBack={goBack} onNext={() => commit({ claudeConnectionMode: state?.claudeConnectionMode ?? 'deferred' }, 3)}
                nextLabel="Continue" extra={
                  <button onClick={() => commit({ claudeConnectionMode: 'deferred' }, 3)} className="text-sm text-white/50 hover:text-white/80">I’ll do this later</button>
                } />
            </StepShell>
          )}

          {/* Step 3 — Protect what matters */}
          {step === 3 && (
            <StepShell eyebrow="Step 3 · Protect what matters" title="Your time, and your life."
              sub="Cadence defends your personal life like a board meeting.">
              <div className="grid grid-cols-2 gap-3">
                <TimeField label="Working hours start" value={workStart} onChange={setWorkStart} />
                <TimeField label="Working hours end" value={workEnd} onChange={setWorkEnd} />
              </div>
              <div className="mt-2 flex flex-col gap-2">
                <ToggleRow label="The school run" hint="Weekday mornings & afternoons, held." checked={schoolRun} onChange={setSchoolRun} />
                <ToggleRow label="Evenings and weekends" hint="No scheduling outside working hours." checked={eveningsWeekends} onChange={setEveningsWeekends} />
                <ToggleRow label="Lunch" hint="A daily protected break." checked={lunch} onChange={setLunch} />
              </div>
              <NavRow onBack={goBack} onNext={commitProtectHours} nextDisabled={saving} />
            </StepShell>
          )}

          {/* Step 4 — Focus rhythm */}
          {step === 4 && (
            <StepShell eyebrow="Step 4 · Your focus rhythm" title="How much deep work each week?" sub="">
              <div className="flex flex-col items-center gap-2 py-2">
                <span className="font-serif text-6xl font-light text-[#7DB1FF]">{focusHours}<span className="text-2xl text-white/50">h</span></span>
                <input type="range" min={4} max={22} value={focusHours} onChange={(e) => setFocusHours(Number(e.target.value))} className="w-full accent-[#7DB1FF]" />
                <span className="text-sm text-white/60">{focusCaption(focusHours)}</span>
              </div>
              <ToggleRow label="Let Cadence allocate it for me" hint="Auto-places focus blocks each morning." checked={autoAllocate} onChange={setAutoAllocate} />
              {hasDrivers === false ? (
                <NavRow onBack={goBack} onNext={() => buildFirstDay({ focusTargetHours: focusHours, autoAllocate })} nextLabel="Build my first day" nextDisabled={saving} />
              ) : (
                <NavRow onBack={goBack} onNext={() => commit({ focusTargetHours: focusHours, autoAllocate }, DRIVERS_STEP)} nextDisabled={saving} />
              )}
            </StepShell>
          )}

          {/* Step 5 — Drivers */}
          {step === DRIVERS_STEP && hasDrivers !== false && (
            <StepShell eyebrow="Step 5 · What guides your year" title="Your strategic drivers."
              sub="The few themes that matter. Every task links to one, so you can see whether your time matches your strategy.">
              <div className="flex flex-wrap gap-2">
                {drivers.map((d) => (
                  <Chip key={d.id} label={d.title} selected={selectedDrivers.includes(d.id)} colour={colourForDriverId(d.id)} onClick={() => toggleDriver(d.id)} />
                ))}
                {drivers.length === 0 && SUGGESTED_DRIVERS.map((t) => (
                  <button key={t} onClick={() => setCustomDriver(t)} className="rounded-full border border-white/15 px-3 py-1 text-sm text-white/70 hover:border-white/40">{t}</button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input value={customDriver} onChange={(e) => setCustomDriver(e.target.value)} placeholder="Add your own…" className="bg-white/[0.04] text-white" />
                <Button variant="outline" onClick={addCustomDriver} disabled={!customDriver.trim()}>Add</Button>
              </div>
              <NavRow onBack={goBack} onNext={() => buildFirstDay()} nextLabel="Build my first day" nextDisabled={saving} />
            </StepShell>
          )}

          {/* Step 6 — Payoff */}
          {step === 6 && (
            <div className="flex flex-col gap-5 pt-6">
              <p className="text-sm uppercase tracking-wide text-white/40">Almost there</p>
              <h1 className="font-serif text-3xl font-light">Claude is building your first day.</h1>
              <div className="flex flex-col gap-2">
                {['Reading your calendar', `Protecting ${focusHours}h of focus around your meetings`,
                  schoolRun ? 'Holding the school run' : 'Holding your protected time',
                  selectedDrivers.length ? 'Aligning your tasks to your drivers' : 'Shaping your day'].map((line, i) => (
                  <div key={line} className="flex items-center gap-2 text-sm">
                    {buildPhase > i ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Loader2 className="h-4 w-4 animate-spin text-white/40" />}
                    <span className={buildPhase > i ? 'text-white' : 'text-white/50'}>{line}</span>
                  </div>
                ))}
              </div>
              {ribbon !== null && (
                <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="mb-1 font-serif text-xl">Tomorrow, protected.</p>
                  {protectedHrs > 0 && <p className="mb-3 text-sm text-[#7DB1FF]">{protectedHrs}h focus held</p>}
                  {ribbon.length > 0
                    ? <DayRibbon segments={ribbon} startHour={hhmmToMinutes(workStart) / 60} endHour={hhmmToMinutes(workEnd) / 60} animate />
                    : <p className="text-sm text-white/60">Your day is set — focus protected around what’s already on your calendar.</p>}
                  <Button onClick={() => setStep(7)} className="mt-4 gap-2 bg-[#7DB1FF] text-[#070A12] hover:bg-[#A9CBFF]">See my day <ArrowRight className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          )}

          {/* Step 7 — Done */}
          {step === 7 && (
            <div className="flex flex-col items-start gap-5 pt-12">
              <p className="text-sm uppercase tracking-wide text-white/40">You’re all set</p>
              <h1 className="font-serif text-4xl font-light">Your time is protected.</h1>
              <p className="max-w-md text-white/70">Cadence will be quiet from here — drafting your mornings, defending your focus, and turning talk into tasks in the background.</p>
              {protectedHrs > 0 && <p className="text-white/80">This week, Cadence has already protected <strong className="text-[#7DB1FF]">{protectedHrs} hours</strong> of your time.</p>}
              <Button onClick={finish} disabled={saving} className="mt-2 gap-2 bg-[#7DB1FF] text-[#070A12] hover:bg-[#A9CBFF]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter Cadence <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ────────────────────────────────────────────

function StepShell({ eyebrow, title, sub, children }: { eyebrow: string; title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 pt-2">
      <p className="text-xs uppercase tracking-wide text-white/40">{eyebrow}</p>
      <h1 className="font-serif text-3xl font-light leading-tight">{title}</h1>
      {sub && <p className="max-w-md text-sm text-white/65">{sub}</p>}
      <div className="mt-2 flex flex-col gap-4">{children}</div>
    </div>
  );
}

function NavRow({ onBack, onNext, nextLabel = 'Continue', nextDisabled, extra }: { onBack: () => void; onNext: () => void; nextLabel?: string; nextDisabled?: boolean; extra?: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-center justify-between">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-white/80"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex items-center gap-3">
        {extra}
        <Button onClick={onNext} disabled={nextDisabled} className="gap-2 bg-[#7DB1FF] text-[#070A12] hover:bg-[#A9CBFF]">{nextLabel} <ArrowRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function ConnectCard({ icon, title, subtitle, busy, done, onClick }: { icon: React.ReactNode; title: string; subtitle: string; busy?: boolean; done?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={busy} className={cn('flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition', done ? 'border-emerald-400/40 bg-emerald-400/5' : 'border-white/[0.12] bg-white/[0.03] hover:border-white/30')}>
      <div className="flex w-full items-center justify-between">{icon}
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-white/20" />}
      </div>
      <div><p className="text-sm font-semibold">{title}</p><p className="text-xs text-white/50">{subtitle}</p></div>
      {done && <span className="text-xs text-emerald-400">Connected</span>}
    </button>
  );
}

function ToggleRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-left">
      <div><p className="text-sm">{label}</p><p className="text-xs text-white/50">{hint}</p></div>
      <span className={cn('relative h-5 w-9 rounded-full transition', checked ? 'bg-[#7DB1FF]' : 'bg-white/15')}>
        <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition', checked ? 'left-[18px]' : 'left-0.5')} />
      </span>
    </button>
  );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-white/50">{label}</Label>
      <Input type="time" value={value} onChange={(e) => onChange(e.target.value)} className="bg-white/[0.04] text-white" />
    </div>
  );
}

function Chip({ label, selected, colour, onClick }: { label: string; selected: boolean; colour: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn('rounded-full border px-3 py-1 text-sm transition', selected ? 'text-[#070A12]' : 'border-white/15 text-white/70 hover:border-white/40')}
      style={selected ? { backgroundColor: colour, borderColor: colour } : undefined}>{label}</button>
  );
}
